import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { usePointerPosition } from '@/hooks/usePointerPosition';

type CursorVariant = 'default' | 'link' | 'view' | 'drag';

/** How fast the trailing ring catches the dot. 1 = instant, 0 = never. */
const FOLLOW = 0.18;

/**
 * Custom cursor — an instant dot plus a ring that lags behind it.
 *
 * The lag is the whole point: a ring that tracks perfectly is invisible as an
 * effect, and one that lags too far feels broken. 0.18 puts it about three
 * frames behind at speed.
 *
 * Hard gates (CLAUDE.md §11): `(pointer: fine)` only — never on touch, where
 * there is no cursor to replace — and off under reduced motion, since a
 * continuously interpolating element is motion by definition. The native
 * cursor is only hidden while this is actually mounted, via the
 * `has-custom-cursor` class, so a gated-off visitor never loses their pointer.
 *
 * Any element can drive it with `data-cursor="view"` and an optional
 * `data-cursor-label`, so sections declare their own affordances instead of
 * this component hardcoding a list of selectors.
 */
export function Cursor() {
  const finePointer = useMediaQuery('(pointer: fine)');
  const reduced = useReducedMotion();
  const enabled = finePointer && !reduced;

  const pointer = usePointerPosition();
  const dotRef = useRef<HTMLDivElement | null>(null);
  const ringRef = useRef<HTMLDivElement | null>(null);
  const [variant, setVariant] = useState<CursorVariant>('default');
  const [label, setLabel] = useState('');
  const [visible, setVisible] = useState(false);

  // Hide the native cursor only while this one is actually running.
  useEffect(() => {
    if (!enabled) return;
    const root = document.documentElement;
    root.classList.add('has-custom-cursor');
    return () => root.classList.remove('has-custom-cursor');
  }, [enabled]);

  // One shared rAF loop; positions written straight to style, never state.
  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    let ringX = 0;
    let ringY = 0;
    let seeded = false;

    const tick = () => {
      const p = pointer.current;
      if (p) {
        if (!seeded) {
          ringX = p.x;
          ringY = p.y;
          seeded = true;
          setVisible(true);
        }
        ringX += (p.x - ringX) * FOLLOW;
        ringY += (p.y - ringY) * FOLLOW;
        if (dotRef.current) {
          dotRef.current.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) translate(-50%, -50%)`;
        }
        if (ringRef.current) {
          ringRef.current.style.transform = `translate3d(${ringX.toFixed(2)}px, ${ringY.toFixed(2)}px, 0) translate(-50%, -50%)`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [enabled, pointer]);

  // Variant detection. `pointerover` bubbles, so one document listener covers
  // everything including content mounted later by a lazy chunk.
  useEffect(() => {
    if (!enabled) return;

    const onOver = (e: PointerEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      const declared = target?.closest<HTMLElement>('[data-cursor]');
      if (declared) {
        const v = declared.dataset.cursor;
        setVariant(v === 'view' || v === 'drag' || v === 'link' ? v : 'default');
        setLabel(declared.dataset.cursorLabel ?? '');
        return;
      }
      if (target?.closest('a, button, [role="button"], input, textarea, select')) {
        setVariant('link');
        setLabel('');
        return;
      }
      setVariant('default');
      setLabel('');
    };

    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    document.addEventListener('pointerover', onOver);
    document.addEventListener('pointerleave', onLeave);
    document.addEventListener('pointerenter', onEnter);
    return () => {
      document.removeEventListener('pointerover', onOver);
      document.removeEventListener('pointerleave', onLeave);
      document.removeEventListener('pointerenter', onEnter);
    };
  }, [enabled]);

  if (!enabled) return null;

  const ringSize =
    variant === 'view' ? 'h-20 w-20' : variant === 'drag' ? 'h-16 w-16' : variant === 'link' ? 'h-11 w-11' : 'h-8 w-8';

  return (
    <div aria-hidden="true" className={cn('pointer-events-none fixed inset-0 z-95', !visible && 'opacity-0')}>
      <div
        ref={ringRef}
        className={cn(
          'ease-settle absolute top-0 left-0 flex items-center justify-center rounded-full border transition-[width,height,background-color,border-color] duration-200',
          ringSize,
          variant === 'default' && 'border-muted',
          variant === 'link' && 'border-acid bg-acid/10',
          (variant === 'view' || variant === 'drag') && 'border-acid bg-acid text-void',
        )}
      >
        {label && <span className="font-mono text-[0.5625rem] tracking-[0.14em] uppercase">{label}</span>}
      </div>
      <div
        ref={dotRef}
        className={cn(
          'bg-acid absolute top-0 left-0 h-1 w-1 rounded-full transition-opacity duration-200',
          variant !== 'default' && 'opacity-0',
        )}
      />
    </div>
  );
}
